import { useState } from "react";
import { FloatButton } from "antd";
import { PlusOutlined, CloseOutlined } from "@ant-design/icons";
import {
  ToteIcon,
  TrolleyIcon,
  LocationPinIcon,
} from "../../assets/icons/icons";
import CodeScanner from "./CodeScanner";
import Icon from "@ant-design/icons";
import "./UniversalScanButton.scss";

interface UniversalScanButtonProps {
  onToteScanned?: (code: string) => void | boolean | Promise<void | boolean>;
  onTrolleyScanned?: (code: string) => void | boolean | Promise<void | boolean>;
  onLocationScanned?: (code: string) => void | boolean | Promise<void | boolean>;
  mode?: "landing" | "tote-info" | "trolley-info" | "location-info";
  isCheckingTote?: boolean;
  isCheckingTrolley?: boolean;
  availableActions?: Array<"tote" | "trolley" | "location">;
  isCheckingLocation?: boolean;
  scanErrorMessage?: string | null;
  onScanErrorClear?: () => void;
  keepScannerOpenOnSuccess?: boolean;
}

const UniversalScanButton = ({
  onToteScanned,
  onTrolleyScanned,
  onLocationScanned,
  mode = "landing",
  isCheckingTote = false,
  isCheckingTrolley = false,
  availableActions,
  isCheckingLocation = false,
  scanErrorMessage,
  onScanErrorClear,
  keepScannerOpenOnSuccess = false,
}: UniversalScanButtonProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [scanType, setScanType] = useState<"tote" | "trolley" | "location">(
    "tote"
  );

  const handleScanSuccess = async (scannedCode: string) => {
    console.log(`Scanned ${scanType}:`, scannedCode);

    let shouldClose = true;

    if (scanType === "tote" && onToteScanned) {
      const result = await onToteScanned(scannedCode);
      if (result === false) {
        shouldClose = false;
      }
    } else if (scanType === "trolley" && onTrolleyScanned) {
      const result = await onTrolleyScanned(scannedCode);
      if (result === false) {
        shouldClose = false;
      }
    } else if (scanType === "location" && onLocationScanned) {
      const result = await onLocationScanned(scannedCode);
      if (result === false) {
        shouldClose = false;
      }
    }

    if (shouldClose) {
      setIsScannerOpen(false);
    }
  };

  const handleToteClick = () => {
    setScanType("tote");
    setIsScannerOpen(true);
    setIsMenuOpen(false);
  };

  const handleTrolleyClick = () => {
    setScanType("trolley");
    setIsScannerOpen(true);
    setIsMenuOpen(false);
  };

  const handleLocationClick = () => {
    setScanType("location");
    setIsScannerOpen(true);
    setIsMenuOpen(false);
  };

  const resolveActions = (): Array<"tote" | "trolley" | "location"> => {
    if (availableActions && availableActions.length > 0) {
      return availableActions;
    }

    if (mode === "tote-info") {
      return ["trolley", "location"];
    }

    if (mode === "trolley-info") {
      return ["tote", "location"];
    }

    if (mode === "location-info") {
      return ["tote", "trolley", "location"];
    }

    return ["tote", "trolley", "location"];
  };

  const getActionLabel = (action: "tote" | "trolley" | "location") => {
    if (mode === "tote-info") {
      if (action === "trolley") return "Load to Trolley";
      if (action === "location") return "Update Location";
    }

    if (mode === "trolley-info") {
      if (action === "tote") return "Load Tote";
      if (action === "location") return "Update Location";
    }

    if (mode === "location-info") {
      if (action === "tote") return "Move Tote to Location";
      if (action === "trolley") return "Move Trolley to Location";
      if (action === "location") return "Scan Location";
    }

    if (action === "tote") return "Scan Tote";
    if (action === "trolley") return "Scan Trolley";
    return "Scan Location";
  };

  const renderActionButton = (action: "tote" | "trolley" | "location") => {
    const label = getActionLabel(action);

    if (action === "tote") {
      return (
        <button
          key="tote"
          className="scan-button scan-tote-button"
          onClick={handleToteClick}
        >
          <Icon component={ToteIcon} />
          <span>{label}</span>
        </button>
      );
    }

    if (action === "trolley") {
      return (
        <button
          key="trolley"
          className="scan-button scan-trolley-button"
          onClick={handleTrolleyClick}
        >
          <Icon component={TrolleyIcon} />
          <span>{label}</span>
        </button>
      );
    }

    return (
      <button
        key="location"
        className="scan-button scan-location-button"
        onClick={handleLocationClick}
      >
        <Icon component={LocationPinIcon} />
        <span>{label}</span>
      </button>
    );
  };

  const actionsToRender = resolveActions();

  return (
    <div className="universal-scan-button-container">
      {isMenuOpen && (
        <div className="scan-backdrop" onClick={() => setIsMenuOpen(false)} />
      )}
      <div className={`scan-buttons ${isMenuOpen ? "open" : ""}`}>
        {actionsToRender.map(renderActionButton)}
      </div>
      <FloatButton
        type="primary"
        style={{ right: 24, bottom: 24 }}
        icon={isMenuOpen ? <CloseOutlined /> : <PlusOutlined />}
        onClick={() => setIsMenuOpen(!isMenuOpen)}
      />

      <CodeScanner
        open={isScannerOpen}
        setOpen={setIsScannerOpen}
        scanType={scanType}
        onScanSuccess={handleScanSuccess}
        isLoading={
          (scanType === "tote" && isCheckingTote) ||
          (scanType === "trolley" && isCheckingTrolley) ||
          (scanType === "location" && isCheckingLocation)
        }
        errorMessage={scanErrorMessage}
        onErrorMessageClear={onScanErrorClear}
        keepOpenOnSuccess={keepScannerOpenOnSuccess}
      />
    </div>
  );
};

export default UniversalScanButton;
